import { LitElement, html, css } from 'lit'

class FormSteps extends LitElement {
  static styles = css`
    ol {
      list-style: none;
      padding: 0;
      display: flex;
      justify-content: space-between;
      margin: 4rem 4rem 5rem;
      position: relative;
    }

    li:after {
      content: '';
      display: block;
      height: 1px;
      background: hsl(var(--theme-400));
      position: absolute;
      left: 4rem;
      right: 4rem;
      top: 31px;
      z-index: 0;
    }

    li {
      display: flex;
      align-items: center;
      flex-direction: column;
    }

    .number {
      background: hsl(var(--theme-150));
      color: hsl(var(--theme-400));
      display: flex;
      justify-content: center;
      align-items: center;
      width: 42px;
      height: 42px;
      border-radius: 50%;
      font-weight: 600;
      margin-bottom: 0.75rem;
      position: relative;
      z-index: 1;
      border: 10px solid hsl(var(--theme-40));
    }

    .title {
      font-size: var(--md);
      font-weight: 400;
      color: hsl(var(--gray-6));
      margin: 0 0.2rem;
    }

    li[aria-selected='true'] .title {
      color: #000;
    }

    li[aria-selected='true'] .number {
      background: hsl(var(--theme-400));
      color: #fff;
    }

    @media (max-width: 768px) {
      ol {
        margin: 2rem 0 4rem;
      }
    }
  `
  static properties = {
    current: { type: Object },
  }

  constructor() {
    super()
    this.steps = this.querySelectorAll('form-step')
    this.current = this.querySelector('form-step[active]')
    this.addEventListener('next-step', this.handleNextStep)
    this.addEventListener('previous-step', this.handlePreviousStep)
    this.steps.forEach((step, i) => {
      step.previousStep = this.steps[i - 1]
      step.nextStep = this.steps[i + 1]
    })
  }

  handleNextStep(e) {
    this.current = e.detail.current
    let next = null
    this.steps.forEach((step, i) => {
      step.active = false
      if (step === this.current) {
        next = this.steps[i + 1]
      }
    })
    if (next) {
      next.active = true
      setTimeout(() => {
        next.querySelector('input').focus()
      })
      this.current = next
    }
  }

  handlePreviousStep(e) {
    this.current = e.detail.current
    let previous = null
    this.steps.forEach((step, i) => {
      step.active = false
      if (step === this.current) {
        previous = this.steps[i - 1]
      }
    })
    if (previous) {
      previous.active = true
      setTimeout(() => {
        previous.querySelector('input').focus()
      })
      this.current = previous
    }
  }

  render() {
    let steps = Array.from(this.steps).map((step, i) => {
      return html`
        <li aria-selected="${step === this.current}">
          <span class="number">${i + 1}</span>
          <span class="title">${step.ariaLabel}</span>
        </li>
      `
    })
    return html`
      <ol class="steps">
        ${steps}
      </ol>
      <slot></slot>
    `
  }
}

customElements.define('form-steps', FormSteps)

class FormStep extends LitElement {
  static styles = css`
    :host(:not([active])) {
      display: none;
    }

    .actions {
      display: flex;
      justify-content: center;
      margin: 4rem 0;
      gap: 1rem;
    }
  `
  static properties = {
    title: { type: String },
    nextButtonText: { type: String },
    backButtonText: { type: String },
    active: { type: Boolean, reflect: true },
    previousStep: { type: Object },
    nextStep: { type: Object },
  }

  constructor() {
    super()
  }

  handleNext() {
    let stepValid = true
    Array.from(this.querySelectorAll('input, textarea, select'))
      .reverse()
      .forEach((input) => {
        if (input.type === 'file') {
          const file = input.files[0]
          if (file && file.size > input.dataset.max) {
            input.setCustomValidity(input.dataset.message)
          }
          else {
            input.setCustomValidity('')
          }
        }
        const valid = input.checkValidity()
        if (!valid) {
          input.reportValidity()
          stepValid = false
        }
      })
    if (stepValid) {
      this.dispatchEvent(
        new CustomEvent('next-step', {
          detail: { current: this },
          bubbles: true,
          composed: true,
        })
      )
    }
  }

  handlePrevious() {
    this.dispatchEvent(
      new CustomEvent('previous-step', {
        detail: { current: this },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    console.log(this.nextStep);
    const nextButton = this.nextStep
      ? html` <button @click="${this.handleNext}" id="nextstep-button" part="button">
          ${this.nextStep?.nextButtonText}
        </button>`
      : ''
    const prevButton = this.previousStep
      ? html` <button @click="${this.handlePrevious}" part="button-secondary">
        ${this.previousStep?.backButtonText}
        </button>`
      : ''

    
    let actions = html`
      <div class="actions">
        ${prevButton} ${nextButton}
        <slot name="actions"></slot>
      </div>
    `

    return html`
      <slot></slot>
      ${actions}
    `
  }
}

customElements.define('form-step', FormStep)

// JavaScript to handle visibility and required attribute dynamically (very specific for pitch form)
document.addEventListener("DOMContentLoaded", function () {
  // Wait for the custom element to be defined
  customElements.whenDefined("form-steps").then(() => {
    const formSteps = document.querySelector("form-step");

    if (formSteps) {
      const shadowRoot = formSteps.shadowRoot;

      if (shadowRoot) {
        // Find the button inside the shadow DOM
        const submitButton = shadowRoot.querySelector('button[part="button"]');

        if (submitButton) {
          addSubmitButtonLogic(submitButton);
        } 
      } 
    } 
  });
  function addSubmitButtonLogic(submitButton) {
    const sectorSelect = document.getElementById("sector");
    const otherSectorInput = document.getElementById("other-sector");
    const otherSectorLabel = document.getElementById("other-sector-label");
    const infoText = document.getElementById("info-text");
    const greentechNo = document.getElementById("greentech_no");
    const swedishBasedNo = document.getElementById("swedishbased_no");
    const greentechYes = document.getElementById("greentech_yes");
    const swedishBasedYes = document.getElementById("swedishbased_yes");

    function toggleOtherSectorInput() {
      if (sectorSelect.value === "other") {
        otherSectorInput.style.display = "block";
        otherSectorLabel.style.display = "block";
        otherSectorInput.setAttribute("required", "true");
      } else {
        otherSectorInput.style.display = "none";
        otherSectorLabel.style.display = "none";
        otherSectorInput.removeAttribute("required");
      }
    }

    function checkFormConditions() {
        if (greentechNo.checked && swedishBasedNo.checked) {
          submitButton.disabled = true;
          infoText.style.display = "flex";
        } else {
          submitButton.disabled = false;
          infoText.style.display = "none";
        }
    }

    // Initial check in case form reloads with "other" pre-selected
    toggleOtherSectorInput();
    checkFormConditions();

    // Listen for changes in the dropdown
    sectorSelect.addEventListener("change", toggleOtherSectorInput);
    greentechNo.addEventListener("change", checkFormConditions);
    swedishBasedNo.addEventListener("change", checkFormConditions);
    greentechYes.addEventListener("change", checkFormConditions);
    swedishBasedYes.addEventListener("change", checkFormConditions);
  }
  
});